import dayjs from "dayjs";
import { collection, getDocs, getFirestore, query, where } from "firebase/firestore";
import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { Card, Col, Form, Row, Spinner } from "react-bootstrap";
import Select from "react-select";

import AnSummary from "components/dashboard/analytics/AnSummary";
import CommonHeaderTabs from "components/dashboard/priceAnalytics/precio/CommonHeaderTabs";
import { DashboardContext } from "context/Context";

const SalesRange = () => {
  const db = getFirestore();
  const { user } = useContext(DashboardContext);

  const [businessLocations, setBusinessLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [startDate, setStartDate] = useState(dayjs().subtract(30, "day").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [loading, setLoading] = useState(false);
  const [dateError, setDateError] = useState("");

  const [magnaProfits, setMagnaProfits] = useState([]);
  const [premiumProfits, setPremiumProfits] = useState([]);
  const [magnaLiters, setMagnaLiters] = useState([]);
  const [premiumLiters, setPremiumLiters] = useState([]);
  const [magnaSales, setMagnaSales] = useState([]);
  const [premiumSales, setPremiumSales] = useState([]);
  const [dateLabels, setDateLabels] = useState([]);
  const [magnaPrices, setMagnaPrices] = useState([]);
  const [premiumPrices, setPremiumPrices] = useState([]);
  const [magnaCosts, setMagnaCosts] = useState([]);
  const [premiumCosts, setPremiumCosts] = useState([]);

  const locationOptions = useMemo(
    () => businessLocations.map((loc) => ({ value: loc, label: loc.name })),
    [businessLocations]
  );

  const fetchLocations = async () => {
    const resp = [];
    const locsRef = collection(db, "companies", user.companyId, "locations");
    const snap = await getDocs(locsRef);
    snap.forEach((doc) => resp.push(doc.data()));
    setBusinessLocations(resp);
    if (resp.length && !selectedLocation) {
      setSelectedLocation({ value: resp[0], label: resp[0].name });
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const toJsDate = (val) => {
    if (!val) return null;
    if (typeof val.toDate === "function") return val.toDate();
    if (val.seconds) return new Date(val.seconds * 1000);
    return new Date(val);
  };

  const loadData = async () => {
    if (!selectedLocation) return;

    const start = dayjs(startDate).startOf("day");
    const end = dayjs(endDate).endOf("day");

    if (!start.isValid() || !end.isValid() || start.isAfter(end)) {
      setDateError("Selecciona un rango de fechas válido");
      return;
    }
    setDateError("");
    setLoading(true);

    try {
      const filesRef = collection(db, "companies", user.companyId, "dayInformation");
      const q = query(
        filesRef,
        where("date", ">=", start.toDate()),
        where("date", "<=", end.toDate())
      );
      const snap = await getDocs(q);

      const records = [];
      snap.forEach((doc) => records.push(doc.data()));

      records.sort((a, b) => {
        const da = toJsDate(a.date)?.getTime() || 0;
        const db = toJsDate(b.date)?.getTime() || 0;
        return da - db;
      });

      const mProfits = [];
      const pProfits = [];
      const mLiters = [];
      const pLiters = [];
      const mSales = [];
      const pSales = [];
      const mPrices = [];
      const pPrices = [];
      const mCosts = [];
      const pCosts = [];
      const labels = [];

      records.forEach((entry) => {
        const jsDate = toJsDate(entry.date);
        const locData = entry?.[selectedLocation.value.locId] || {};

        if (
          locData.magnaSalesIncome !== undefined &&
          locData.magnaLitersSold !== undefined &&
          locData.magnaPrice !== undefined &&
          locData.magnaCost !== undefined
        ) {
          const profit = locData.magnaSalesIncome - locData.magnaLitersSold * locData.magnaCost;
          mProfits.push(profit);
          mLiters.push(locData.magnaLitersSold);
          mSales.push(locData.magnaSalesIncome);
          mPrices.push(locData.magnaPrice);
          mCosts.push(locData.magnaCost);
          labels.push(jsDate ? jsDate.toLocaleDateString("es-MX") : "");
        }

        if (
          locData.premiumSalesIncome !== undefined &&
          locData.premiumLitersSold !== undefined &&
          locData.premiumPrice !== undefined &&
          locData.premiumCost !== undefined
        ) {
          const profit =
            locData.premiumSalesIncome - locData.premiumLitersSold * locData.premiumCost;
          pProfits.push(profit);
          pLiters.push(locData.premiumLitersSold);
          pSales.push(locData.premiumSalesIncome);
          pPrices.push(locData.premiumPrice);
          pCosts.push(locData.premiumCost);
        }
      });

      const ensureData = (arr, filler = 0) => (arr.length ? arr : [filler]);
      const ensureLabels = (arr) => (arr.length ? arr : ["Sin datos"]);

      setMagnaProfits(ensureData(mProfits));
      setPremiumProfits(ensureData(pProfits));
      setMagnaLiters(ensureData(mLiters));
      setPremiumLiters(ensureData(pLiters));
      setMagnaSales(ensureData(mSales));
      setPremiumSales(ensureData(pSales));
      setDateLabels(ensureLabels(labels));
      setMagnaPrices(ensureData(mPrices));
      setPremiumPrices(ensureData(pPrices));
      setMagnaCosts(ensureData(mCosts));
      setPremiumCosts(ensureData(pCosts));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedLocation, startDate, endDate]);

  return (
    <Fragment>
      <CommonHeaderTabs />
      <Card className="mb-4">
        <Card.Header className="d-flex flex-wrap align-items-center justify-content-between">
          <div>
            <h3 className="mb-1">Ventas por rango</h3>
            <small>
              Elige la estación y el rango de fechas para graficar ventas, litros y utilidad.
            </small>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div>
              <Form.Label className="mb-0 small">Fecha inicio</Form.Label>
              <Form.Control
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="sm"
              />
            </div>
            <div>
              <Form.Label className="mb-0 small">Fecha fin</Form.Label>
              <Form.Control
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                size="sm"
              />
            </div>
            <div style={{ minWidth: "16rem" }}>
              <Form.Label className="mb-0 small">Estación</Form.Label>
              <Select
                classNamePrefix="react-select"
                isClearable={false}
                placeholder="Selecciona"
                value={selectedLocation}
                options={locationOptions}
                onChange={(opt) => setSelectedLocation(opt)}
              />
            </div>
          </div>
        </Card.Header>
        {dateError && <div className="text-danger px-3 pt-2">{dateError}</div>}
        <Card.Body>
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" role="status" />
            </div>
          ) : (
            <Row className="g-4">
              <Col xs={12}>
                <AnSummary
                  ventaData={magnaSales}
                  litrosData={magnaLiters}
                  utilidadData={magnaProfits}
                  xAxisData={dateLabels}
                  priceData={magnaPrices}
                  costData={magnaCosts}
                  title="Regular"
                />
              </Col>
              <Col xs={12}>
                <AnSummary
                  ventaData={premiumSales}
                  litrosData={premiumLiters}
                  utilidadData={premiumProfits}
                  xAxisData={dateLabels}
                  priceData={premiumPrices}
                  costData={premiumCosts}
                  title="Premium"
                />
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
    </Fragment>
  );
};

export default SalesRange;
